/**
 * DELETED ITEMS — super admin only. What was removed, and the way back.
 *
 * Nothing is ever erased: a removed member or gift card is hidden from the
 * clinic and restored from here.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, X } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { downloadConsentCsv } from './consentCsv';
import { money } from '../../lib/money';

const when = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

export const DeletedItems = ({ organizationId, onClose, onChanged }) => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        try {
            const r = await api.get('/loyalty/deleted',
                { params: { organization_id: organizationId } });
            setData(r.data);
        } catch (e) {
            setError(e.response?.data?.detail || t('common.error', 'Something went wrong'));
        }
    }, [organizationId, t]);

    useEffect(() => { load(); }, [load]);

    const restore = async (kind, id) => {
        setBusy(true); setError('');
        try {
            await api.post(kind === 'member'
                ? `/loyalty/members/${id}/restore`
                : `/loyalty/gift-cards/${id}/restore`);
            await load();
            onChanged?.();
        } catch (e) {
            setError(e.response?.data?.detail || t('common.error', 'Something went wrong'));
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 p-3 sm:p-6"
             data-testid="loyalty-deleted-screen">
            <div className="mx-auto max-w-2xl rounded-2xl bg-white p-5 shadow-xl dark:bg-slate-900">
                <div className="mb-4 flex items-start justify-between gap-3">
                    <h3 className="text-lg font-bold">
                        {t('loyalty.deleted.title', 'Deleted items')}
                    </h3>
                    <Button size="sm" variant="ghost" onClick={onClose}
                            data-testid="loyalty-deleted-close">
                        <X className="h-4 w-4" />
                    </Button>
                </div>

                {!data && <Loader2 className="mx-auto h-5 w-5 animate-spin" />}

                {data && (
                    <div className="space-y-5">
                        <div>
                            <p className="mb-1 text-xs font-semibold">
                                {t('loyalty.deleted.members', 'Removed members')}
                            </p>
                            {(data.members || []).length === 0 ? (
                                <p className="text-xs text-slate-500" data-testid="loyalty-deleted-members-empty">
                                    {t('loyalty.deleted.none', 'Nothing has been removed.')}
                                </p>
                            ) : (
                                <ul className="space-y-1" data-testid="loyalty-deleted-members">
                                    {(data.members || []).map((m) => (
                                        <li key={m.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg
                                                                  border border-slate-200 px-3 py-2 text-sm
                                                                  dark:border-slate-700">
                                            <span>{m.first_name} · {m.email}
                                                <span className="ml-2 text-xs text-slate-500">
                                                    {when(m.deleted_at)}{m.deleted_by ? ` · ${m.deleted_by}` : ''}
                                                </span>
                                            </span>
                                            <Button size="sm" variant="outline" disabled={busy}
                                                    data-testid={`loyalty-consent-csv-${m.id}`}
                                                    onClick={() => downloadConsentCsv(organizationId, m.id)}>
                                                {t('loyalty.consent.downloadOne', 'Download this consent (CSV)')}
                                            </Button>
                                            <Button size="sm" variant="outline" disabled={busy}
                                                    data-testid={`loyalty-restore-member-${m.id}`}
                                                    onClick={() => restore('member', m.id)}>
                                                {t('loyalty.deleted.restore', 'Restore')}
                                            </Button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <div>
                            <p className="mb-1 text-xs font-semibold">
                                {t('loyalty.deleted.cards', 'Removed gift cards')}
                            </p>
                            {(data.gift_cards || []).length === 0 ? (
                                <p className="text-xs text-slate-500" data-testid="loyalty-deleted-cards-empty">
                                    {t('loyalty.deleted.none', 'Nothing has been removed.')}
                                </p>
                            ) : (
                                <ul className="space-y-1" data-testid="loyalty-deleted-cards">
                                    {(data.gift_cards || []).map((c) => (
                                        <li key={c.id} className="flex items-center justify-between gap-2 rounded-lg
                                                                  border border-slate-200 px-3 py-2 text-sm
                                                                  dark:border-slate-700">
                                            <span>{c.code} · {money(c.value)}
                                                <span className="ml-2 text-xs text-slate-500">{when(c.deleted_at)}</span>
                                            </span>
                                            <Button size="sm" variant="outline" disabled={busy}
                                                    data-testid={`loyalty-restore-card-${c.id}`}
                                                    onClick={() => restore('card', c.id)}>
                                                {t('loyalty.deleted.restore', 'Restore')}
                                            </Button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <div>
                            <p className="mb-1 text-xs font-semibold">
                                {t('loyalty.deleted.requests', 'Members who asked to be removed')}
                            </p>
                            {(data.requests || []).length === 0 ? (
                                <p className="text-xs text-slate-500" data-testid="loyalty-deleted-requests-empty">
                                    {t('loyalty.deleted.noRequests', 'Nobody has asked.')}
                                </p>
                            ) : (
                                <ul className="space-y-1" data-testid="loyalty-deleted-requests">
                                    {(data.requests || []).map((m) => (
                                        <li key={m.id} className="rounded-lg border border-amber-200 px-3 py-2 text-sm
                                                                  dark:border-amber-800">
                                            {m.first_name} · {m.email}
                                            <span className="ml-2 text-xs text-slate-500">
                                                {when(m.delete_requested_at)}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )}

                {error && <p className="mt-3 text-xs text-rose-500"
                             data-testid="loyalty-deleted-error">{error}</p>}
            </div>
        </div>
    );
};

export default DeletedItems;
